import { useState } from "react"
import {
  Search,
  Plus,
  Pencil,
  Trash2,
  ToggleLeft,
  ToggleRight,
  RefreshCw,
  Trash,
} from "lucide-react"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table"
import { Badge } from "../components/ui/badge"
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "../components/ui/dialog"
import { useLocale } from "../components/LocaleProvider"
import { toast } from "../components/ui/toast"
import {
  useProfiles,
  useCreateProfile,
  useUpdateProfile,
  useToggleProfile,
  useDeleteProfile,
  useDeleteAllProfiles,
  useSyncProfilesFromClients,
} from "../lib/queries"
import type { Profile } from "../types"
import type { TranslationKey } from "../i18n/translations"

const emptyForm = {
  name: "",
  maxRent: "",
  minRooms: "",
  maxRooms: "",
  minSize: "",
  maxSize: "",
  familySize: "1",
  desireLocation: [] as string[],
  goodLocation: [] as string[],
  optimallyLocation: [] as string[],
  notes: "",
}

const HAMBURG_AREAS = [
  "Altona",
  "Altona-Altstadt",
  "Bahrenfeld",
  "Blankenese",
  "Barmbek",
  "Billstedt",
  "Bergedorf",
  "Bramfeld",
  "Dulsberg",
  "Eidelstedt",
  "Eimsbüttel",
  "Eilbek",
  "Farmsen",
  "Farmsen-Berne",
  "Fuhlsbüttel",
  "Groß Flottbek",
  "Hamm",
  "Harburg",
  "Horn",
  "Iserbrook",
  "Jenfeld",
  "Langenhorn",
  "Lokstedt",
  "Lurup",
  "Marienthal",
  "Neugraben",
  "Niendorf",
  "Nienstedten",
  "Ohlsdorf",
  "Osdorf",
  "Othmarschen",
  "Ottensen",
  "Poppenbüttel",
  "Rahlstedt",
  "Rissen",
  "Sasel",
  "Schnelsen",
  "Sternschanze",
  "Steilshoop",
  "Sülldorf",
  "Stellingen",
  "Tonndorf",
  "Wandsbek",
  "Wellingsbüttel",
  "Wilhelmsburg",
  "Winterhude",
]

function LocationPicker({
  label,
  selected,
  onChange,
}: {
  label: string
  selected: string[]
  onChange: (v: string[]) => void
}) {
  const { t } = useLocale()
  return (
    <div className="space-y-1.5">
      <label className="text-sm font-medium">{label}</label>
      <div className="flex flex-wrap gap-1.5">
        {HAMBURG_AREAS.map((area) => {
          const isSelected = selected.includes(area)
          return (
            <label
              key={area}
              className={`inline-flex items-center gap-1 px-2 py-1 rounded border text-xs cursor-pointer transition-colors ${
                isSelected
                  ? "border-blue-500 bg-blue-50 text-blue-800 dark:bg-blue-950 dark:text-blue-200 dark:border-blue-400"
                  : "border-input bg-background hover:bg-accent"
              }`}
            >
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => {
                  onChange(isSelected ? selected.filter((a) => a !== area) : [...selected, area])
                }}
                className="sr-only"
              />
              {area}
            </label>
          )
        })}
      </div>
    </div>
  )
}

function Profiles() {
  const { data: profilesData, isLoading } = useProfiles()
  const profiles: Profile[] = profilesData ?? []
  const createMut = useCreateProfile()
  const updateMut = useUpdateProfile()
  const toggleMut = useToggleProfile()
  const deleteMut = useDeleteProfile()
  const deleteAllMut = useDeleteAllProfiles()
  const syncMut = useSyncProfilesFromClients()
  const { t } = useLocale()
  const [search, setSearch] = useState("")
  const [open, setOpen] = useState(false)
  const [deleteAllOpen, setDeleteAllOpen] = useState(false)
  const [form, setForm] = useState(emptyForm)
  const [editingId, setEditingId] = useState<number | null>(null)

  const filtered = profiles.filter((p) => p.name.toLowerCase().includes(search.toLowerCase()))

  const openAdd = () => {
    setEditingId(null)
    setForm(emptyForm)
    setOpen(true)
  }

  const openEdit = (id: number) => {
    const p = profiles.find((x) => x.id === id)
    if (!p) return
    setEditingId(id)
    setForm({
      name: p.name,
      maxRent: p.maxRent?.toString() || "",
      minRooms: p.minRooms?.toString() || "",
      maxRooms: p.maxRooms?.toString() || "",
      minSize: p.minSize?.toString() || "",
      maxSize: p.maxSize?.toString() || "",
      familySize: p.familySize.toString(),
      desireLocation: p.desireLocation || [],
      goodLocation: p.goodLocation || [],
      optimallyLocation: p.optimallyLocation || [],
      notes: p.notes,
    })
    setOpen(true)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const dto = {
      name: form.name,
      maxRent: form.maxRent ? parseFloat(form.maxRent) : undefined,
      minRooms: form.minRooms ? parseFloat(form.minRooms) : undefined,
      maxRooms: form.maxRooms ? parseFloat(form.maxRooms) : undefined,
      minSize: form.minSize ? parseFloat(form.minSize) : undefined,
      maxSize: form.maxSize ? parseFloat(form.maxSize) : undefined,
      familySize: parseInt(form.familySize) || 1,
      desireLocation: form.desireLocation,
      goodLocation: form.goodLocation,
      optimallyLocation: form.optimallyLocation,
      notes: form.notes,
    }
    if (editingId !== null) {
      updateMut.mutate({ id: editingId, dto })
    } else {
      createMut.mutate(dto)
    }
    setOpen(false)
    setForm(emptyForm)
    setEditingId(null)
  }

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center h-64">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("search_clients")}
            className="h-10 w-full max-w-sm rounded-md border border-input bg-background pl-10 pr-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => syncMut.mutate()}
            disabled={syncMut.isPending}
            className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md border border-input bg-background text-sm font-medium hover:bg-accent transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${syncMut.isPending ? "animate-spin" : ""}`} />
            {t("btn_sync_clients")}
          </button>
          <Dialog open={deleteAllOpen} onOpenChange={setDeleteAllOpen}>
            <DialogTrigger asChild>
              <button className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md border border-destructive text-destructive bg-background text-sm font-medium hover:bg-destructive/10 transition-colors">
                <Trash className="h-4 w-4" />
                {t("btn_delete_all")}
              </button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{t("dialog_confirm_delete_all")}</DialogTitle>
              </DialogHeader>
              <div className="flex justify-end gap-3 pt-2">
                <DialogClose asChild>
                  <button className="h-9 px-4 rounded-md border border-input bg-background text-sm hover:bg-accent transition-colors">
                    {t("btn_cancel")}
                  </button>
                </DialogClose>
                <button
                  onClick={() => {
                    deleteAllMut.mutate()
                    setDeleteAllOpen(false)
                  }}
                  className="h-9 px-4 rounded-md bg-destructive text-destructive-foreground text-sm font-medium hover:bg-destructive/90 transition-colors"
                >
                  {t("btn_delete")}
                </button>
              </div>
            </DialogContent>
          </Dialog>
          <Dialog
            open={open}
            onOpenChange={(v) => {
              if (!v) {
                setOpen(false)
                setEditingId(null)
              } else setOpen(true)
            }}
          >
            <DialogTrigger asChild>
              <button
                onClick={openAdd}
                className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
              >
                <Plus className="h-4 w-4" />
                {t("btn_add_record")}
              </button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {editingId !== null ? t("dialog_edit_profile") : t("dialog_add_profile")}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">{t("form_profile_name")}</label>
                  <input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    required
                    className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium">{t("form_max_rent")}</label>
                    <input
                      type="number"
                      value={form.maxRent}
                      onChange={(e) => setForm({ ...form, maxRent: e.target.value })}
                      className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium">{t("form_min_rooms")}</label>
                    <input
                      type="number"
                      step="0.5"
                      value={form.minRooms}
                      onChange={(e) => setForm({ ...form, minRooms: e.target.value })}
                      className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium">{t("form_max_rooms")}</label>
                    <input
                      type="number"
                      step="0.5"
                      value={form.maxRooms}
                      onChange={(e) => setForm({ ...form, maxRooms: e.target.value })}
                      className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium">{t("form_min_size")}</label>
                    <input
                      type="number"
                      value={form.minSize}
                      onChange={(e) => setForm({ ...form, minSize: e.target.value })}
                      className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium">{t("form_max_size")}</label>
                    <input
                      type="number"
                      value={form.maxSize}
                      onChange={(e) => setForm({ ...form, maxSize: e.target.value })}
                      className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium">{t("form_family_size")}</label>
                    <input
                      type="number"
                      min="1"
                      value={form.familySize}
                      onChange={(e) => setForm({ ...form, familySize: e.target.value })}
                      className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    />
                  </div>
                </div>
                <LocationPicker
                  label={t("th_desire_location")}
                  selected={form.desireLocation}
                  onChange={(v) => setForm({ ...form, desireLocation: v })}
                />
                <LocationPicker
                  label={t("th_good_location")}
                  selected={form.goodLocation}
                  onChange={(v) => setForm({ ...form, goodLocation: v })}
                />
                <LocationPicker
                  label={t("th_optimally_location")}
                  selected={form.optimallyLocation}
                  onChange={(v) => setForm({ ...form, optimallyLocation: v })}
                />
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">{t("form_notes")}</label>
                  <textarea
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    rows={2}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-none"
                  />
                </div>
                <div className="flex justify-end gap-3 pt-2">
                  <DialogClose asChild>
                    <button
                      type="button"
                      className="h-9 px-4 rounded-md border border-input bg-background text-sm hover:bg-accent transition-colors"
                    >
                      {t("btn_cancel")}
                    </button>
                  </DialogClose>
                  <button
                    type="submit"
                    className="h-9 px-4 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
                  >
                    {t("btn_save")}
                  </button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="rounded-lg border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">{t("th_id")}</TableHead>
              <TableHead>{t("th_profile_name")}</TableHead>
              <TableHead>{t("th_max_rent")}</TableHead>
              <TableHead>{t("th_rooms_range")}</TableHead>
              <TableHead>{t("th_size_range")}</TableHead>
              <TableHead>{t("th_family_size")}</TableHead>
              <TableHead>{t("th_desire_location")}</TableHead>
              <TableHead>{t("th_good_location")}</TableHead>
              <TableHead>{t("th_matches")}</TableHead>
              <TableHead>{t("th_active")}</TableHead>
              <TableHead className="w-24">{t("th_actions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((row) => (
              <TableRow key={row.id}>
                <TableCell className="font-mono text-xs text-muted-foreground">{row.id}</TableCell>
                <TableCell className="font-medium">{row.name}</TableCell>
                <TableCell>{row.maxRent != null ? `${row.maxRent} €` : "—"}</TableCell>
                <TableCell>
                  {row.minRooms != null || row.maxRooms != null
                    ? `${row.minRooms ?? 0} – ${row.maxRooms ?? "∞"}`
                    : "—"}
                </TableCell>
                <TableCell>
                  {row.minSize != null || row.maxSize != null
                    ? `${row.minSize ?? 0} – ${row.maxSize ?? "∞"} m²`
                    : "—"}
                </TableCell>
                <TableCell>{row.familySize}</TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {(row.desireLocation || []).slice(0, 2).map((l) => (
                      <Badge key={l} variant="info" className="text-xs">
                        {l}
                      </Badge>
                    ))}
                    {(row.desireLocation || []).length > 2 && (
                      <Badge variant="neutral" className="text-xs">
                        +{row.desireLocation.length - 2}
                      </Badge>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {(row.goodLocation || []).slice(0, 2).map((l) => (
                      <Badge key={l} variant="success" className="text-xs">
                        {l}
                      </Badge>
                    ))}
                    {(row.goodLocation || []).length > 2 && (
                      <Badge variant="neutral" className="text-xs">
                        +{row.goodLocation.length - 2}
                      </Badge>
                    )}
                  </div>
                </TableCell>
                <TableCell className="font-mono text-xs">{row._count?.matches ?? 0}</TableCell>
                <TableCell>
                  <button
                    onClick={() => toggleMut.mutate(row.id)}
                    className="hover:opacity-80 transition-opacity"
                  >
                    {row.active ? (
                      <ToggleRight className="h-5 w-5 text-green-600" />
                    ) : (
                      <ToggleLeft className="h-5 w-5 text-muted-foreground" />
                    )}
                  </button>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => openEdit(row.id)}
                      className="h-8 w-8 rounded-md border border-input bg-background flex items-center justify-center hover:bg-accent transition-colors"
                      title={t("tip_edit")}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={() => deleteMut.mutate(row.id)}
                      className="h-8 w-8 rounded-md border border-input bg-background flex items-center justify-center hover:bg-accent transition-colors text-destructive"
                      title={t("btn_delete")}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}

export default Profiles
